import React, { useEffect, useState } from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { ShieldIcon, BarChartIcon, ScrollIcon, BuildingIcon, CheckCircleIcon, AwardIcon, ArrowRightIcon, GraduationCapIcon, HeartIcon, FacebookIcon, InstagramIcon } from 'lucide-react';
const Home = () => {
  const [isVisible, setIsVisible] = useState({
    promise: false,
    whyUs: false,
    services: false,
    insights: false
  });
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['promise', 'why-us', 'services', 'insights'];
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          const position = section.getBoundingClientRect();
          if (position.top < window.innerHeight * 0.75) {
            setIsVisible(prev => ({
              ...prev,
              [sectionId.replace('-', '')]: true
            }));
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-secondary" data-id="element-408">
      {/* Hero Section - Updated with Singapore skyline */}
      <section className="min-h-screen bg-cover bg-center flex items-center relative pt-32 pb-20" data-id="element-409">
        <div className="absolute inset-0 bg-secondary/60" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
        backgroundBlendMode: 'overlay',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} data-id="element-410"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-411">
          <div className="max-w-4xl mx-auto text-center" data-id="element-412">
            {/* Prominent Branding */}
            <div className="mb-12 animate-scale-in" data-id="element-413">
              <h1 className="font-display font-black text-5xl md:text-7xl tracking-widest mb-4 text-primary" data-id="element-414">
                MAGNAVERA GROUP
              </h1>
              <div className="h-1 w-32 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto" data-id="element-415"></div>
            </div>

            <h2 className="text-text-primary text-2xl md:text-4xl font-display font-bold mb-6 leading-tight tracking-wide animate-fade-in-up" data-id="element-416">
              NAVIGATING WEALTH WITH CONFIDENCE
            </h2>

            <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-body animate-fade-in-up" style={{
            animationDelay: '0.2s'
          }} data-id="element-417">
              Expert advisory services tailored to your unique needs and
              circumstances, delivered with integrity and precision.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{
            animationDelay: '0.4s'
          }} data-id="element-418">
              <Button href="/contact" className="shadow-gold-md" data-id="element-419">
                GET IN TOUCH
              </Button>
              <Button href="/about" variant="secondary" data-id="element-420">
                LEARN MORE
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise Section */}
      <section id="promise" className="py-20 bg-background" data-id="element-421">
        <div className="container mx-auto px-4" data-id="element-422">
          <div className="max-w-4xl mx-auto" data-id="element-423">
            <div className={`gradient-border p-10 md:p-16 transition-all duration-700 ${isVisible.promise ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-424">
              <div className="text-center" data-id="element-425">
                <span className="inline-block text-primary font-display font-bold mb-4 uppercase tracking-widest text-sm" data-id="element-426">
                  OUR PROMISE
                </span>
                <h2 className="text-text-primary font-display font-bold mb-6 text-3xl md:text-4xl" data-id="element-427">
                  BUILDING FINANCIAL FREEDOM TOGETHER
                </h2>
                <div className="w-24 h-1 bg-primary mx-auto mb-8" data-id="element-428"></div>
                <p className="text-text-secondary text-lg leading-relaxed mb-8 font-body" data-id="element-429">
                  At Magnavera Group, we believe that financial freedom is built
                  on education, ethics, and empowerment. Our advisors go beyond
                  transactions — we partner with you for the long term, helping
                  you make confident, informed decisions at every stage of life.
                </p>
                <Button href="/about" variant="secondary" data-id="element-430">
                  LEARN MORE ABOUT US
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="py-20 bg-secondary" data-id="element-431">
        <div className="container mx-auto px-4" data-id="element-432">
          <div className="text-center mb-16" data-id="element-433">
            <span className="inline-block text-primary font-display font-bold mb-4 uppercase tracking-widest text-sm" data-id="element-434">
              WHY CHOOSE US
            </span>
            <h2 className="text-text-primary font-display font-bold mb-4 text-3xl md:text-4xl" data-id="element-435">
              THE MAGNAVERA ADVANTAGE
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto" data-id="element-436"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-437">
            {[{
            icon: <ShieldIcon size={40} data-id="element-438" />,
            title: 'Trusted Guidance',
            description: 'Built on integrity and transparency, our advisors provide honest, straightforward guidance you can rely on.',
            delay: 0
          }, {
            icon: <AwardIcon size={40} data-id="element-439" />,
            title: 'Proven Expertise',
            description: 'Our advisors are recognized for MDRT and industry excellence, bringing professional knowledge to every client.',
            delay: 100
          }, {
            icon: <GraduationCapIcon size={40} data-id="element-440" />,
            title: 'Culture of Growth',
            description: 'A team environment that nurtures leaders and celebrates achievements, ensuring continuous improvement.',
            delay: 200
          }, {
            icon: <HeartIcon size={40} data-id="element-441" />,
            title: 'Community Commitment',
            description: 'Actively engaged in social responsibility and peacebuilding initiatives that strengthen our communities.',
            delay: 300
          }].map((item, index) => <div key={index} className={`transition-all duration-700 ${isVisible.whyUs ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: `${item.delay}ms`
          }} data-id="element-442">
                <Card title={item.title} icon={item.icon} className="h-full" data-id="element-443">
                  {item.description}
                </Card>
              </div>)}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-background to-secondary" data-id="element-444">
        <div className="container mx-auto px-4" data-id="element-445">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-446">
            {[{
            number: '15+',
            label: 'YEARS OF EXPERIENCE'
          }, {
            number: '5,000+',
            label: 'SATISFIED CLIENTS'
          }, {
            number: '98%',
            label: 'CLIENT RETENTION'
          }, {
            number: '24/7',
            label: 'CLIENT SUPPORT'
          }].map((stat, index) => <div key={index} className="text-center group" data-id="element-447">
                <div className="stat-number mb-3 group-hover:animate-glow-pulse" data-id="element-448">
                  {stat.number}
                </div>
                <p className="text-text-secondary font-display uppercase tracking-widest text-sm font-semibold" data-id="element-449">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section id="services" className="py-20 bg-secondary" data-id="element-450">
        <div className="container mx-auto px-4" data-id="element-451">
          <div className="text-center mb-16" data-id="element-452">
            <span className="inline-block text-primary font-display font-bold mb-4 uppercase tracking-widest text-sm" data-id="element-453">
              OUR EXPERTISE
            </span>
            <h2 className="text-text-primary font-display font-bold mb-4 text-3xl md:text-4xl" data-id="element-454">
              PREMIUM SERVICES
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto" data-id="element-455"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-456">
            {[{
            icon: <BarChartIcon size={40} data-id="element-457" />,
            title: 'Wealth & Investment',
            description: 'Portfolio creation, SRS optimization, and family office structuring for comprehensive wealth management.',
            link: {
              text: 'LEARN MORE',
              url: '/services'
            },
            delay: 0
          }, {
            icon: <ShieldIcon size={40} data-id="element-458" />,
            title: 'Insurance & Protection',
            description: 'Life, health, legacy planning, and retirement coverage to secure your future and loved ones.',
            link: {
              text: 'LEARN MORE',
              url: '/services'
            },
            delay: 100
          }, {
            icon: <ScrollIcon size={40} data-id="element-459" />,
            title: 'Estate & Legacy',
            description: 'Wills, LPA, and multi-generational wealth planning to ensure your legacy continues as intended.',
            link: {
              text: 'LEARN MORE',
              url: '/services'
            },
            delay: 200
          }, {
            icon: <BuildingIcon size={40} data-id="element-460" />,
            title: 'Corporate Solutions',
            description: 'Employee benefits, business insurance, Work Injury Compensation, and Buy-Sell Agreements.',
            link: {
              text: 'LEARN MORE',
              url: '/services'
            },
            delay: 300
          }].map((service, index) => <div key={index} className={`transition-all duration-700 ${isVisible.services ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: `${service.delay}ms`
          }} data-id="element-461">
                <Card title={service.title} icon={service.icon} link={service.link} className="h-full" data-id="element-462">
                  {service.description}
                </Card>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-463">
            <Button href="/services" variant="secondary" data-id="element-464">
              VIEW ALL SERVICES
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Case Study */}
      <section className="py-20 bg-background" data-id="element-465">
        <div className="container mx-auto px-4" data-id="element-466">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-467">
            <div data-id="element-468">
              <span className="inline-block text-primary font-display font-bold mb-4 uppercase tracking-widest text-sm" data-id="element-469">
                OUR APPROACH
              </span>
              <h2 className="text-text-primary font-display font-bold mb-6 text-3xl md:text-4xl" data-id="element-470">
                STRATEGIC ADVISORY PROCESS
              </h2>
              <p className="text-text-secondary mb-6 leading-relaxed font-body" data-id="element-471">
                At Magnavera Group, we believe in a comprehensive approach to
                advisory services. Our process is designed to understand your
                unique situation, identify potential risks, and develop tailored
                solutions that provide optimal protection.
              </p>

              <div className="space-y-4 mb-8" data-id="element-472">
                {['Thorough needs assessment and risk analysis', 'Customized solution development', 'Regular portfolio reviews and adjustments', 'Proactive communication and support'].map((item, index) => <div key={index} className="flex items-start" data-id="element-473">
                    <div className="bg-primary/20 p-2 rounded text-primary mr-3 mt-1" data-id="element-474">
                      <CheckCircleIcon size={20} data-id="element-475" />
                    </div>
                    <span className="text-text-primary font-body" data-id="element-476">{item}</span>
                  </div>)}
              </div>

              <Button href="/contact" data-id="element-477">SCHEDULE A CONSULTATION</Button>
            </div>

            <div className="relative" data-id="element-478">
              <div className="gradient-border p-2" data-id="element-479">
                <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80" alt="Strategic Advisory Process" className="rounded w-full" data-id="element-480" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gradient-gold p-8 rounded shadow-gold-lg hidden md:block" data-id="element-481">
                <div className="text-5xl font-black mb-2 font-display text-gold-glow" data-id="element-482">
                  98%
                </div>
                <div className="text-sm font-semibold font-display text-text-primary tracking-wider" data-id="element-483">
                  CLIENT SATISFACTION
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Insights - Updated with working images */}
      <section id="insights" className="py-20 bg-secondary" data-id="element-484">
        <div className="container mx-auto px-4" data-id="element-485">
          <div className="text-center mb-16" data-id="element-486">
            <span className="inline-block text-primary font-display font-bold mb-4 uppercase tracking-widest text-sm" data-id="element-487">
              STAY INFORMED
            </span>
            <h2 className="text-text-primary font-display font-bold mb-4 text-3xl md:text-4xl" data-id="element-488">
              FEATURED INSIGHTS & EVENTS
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto" data-id="element-489"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-490">
            {[{
            platform: 'Facebook',
            icon: <FacebookIcon size={24} className="text-[#1877F2]" data-id="element-491" />,
            title: 'Financial Planning Workshop',
            date: 'June 15, 2023',
            excerpt: 'Join us for an interactive session on essential financial planning strategies for young professionals.',
            imageUrl: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=600',
            delay: 0
          }, {
            platform: 'Instagram',
            icon: <InstagramIcon size={24} className="text-[#E1306C]" data-id="element-492" />,
            title: 'Client Appreciation Day',
            date: 'July 8, 2023',
            excerpt: 'Thank you to all our amazing clients who joined us for our annual appreciation event!',
            imageUrl: 'https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=600',
            delay: 100
          }, {
            platform: 'Facebook',
            icon: <FacebookIcon size={24} className="text-[#1877F2]" data-id="element-493" />,
            title: 'Retirement Planning Seminar',
            date: 'August 22, 2023',
            excerpt: 'Discover strategies to ensure a comfortable retirement with our expert advisors.',
            imageUrl: 'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg?auto=compress&cs=tinysrgb&w=600',
            delay: 200
          }].map((post, index) => <div key={index} className={`gradient-border overflow-hidden transition-all duration-700 hover:shadow-gold-md ${isVisible.insights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: `${post.delay}ms`
          }} data-id="element-494">
                <img src={post.imageUrl} alt={post.title} className="w-full h-48 object-cover" data-id="element-495" />
                <div className="p-6 bg-surface" data-id="element-496">
                  <div className="flex items-center mb-3" data-id="element-497">
                    {post.icon}
                    <span className="text-sm text-text-secondary ml-2 font-body" data-id="element-498">
                      {post.platform}
                    </span>
                    <span className="text-xs text-text-secondary/60 ml-auto font-body" data-id="element-499">
                      {post.date}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-lg mb-2 text-text-primary uppercase tracking-wide" data-id="element-500">
                    {post.title}
                  </h3>
                  <p className="text-text-secondary mb-4 font-body" data-id="element-501">
                    {post.excerpt}
                  </p>
                  <a href="#" className="text-primary font-display font-semibold inline-flex items-center hover:text-gold-glow transition-colors text-sm tracking-wider group" data-id="element-502">
                    READ MORE
                    <ArrowRightIcon size={16} className="ml-2 transition-transform group-hover:translate-x-1" data-id="element-503" />
                  </a>
                </div>
              </div>)}
          </div>

          <div className="text-center mt-10" data-id="element-504">
            <Button href="#" variant="secondary" data-id="element-505">
              VIEW ALL INSIGHTS
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Banner - Fixed button readability */}
      <section className="py-20 bg-gradient-gold relative overflow-hidden" data-id="element-506">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
      }} data-id="element-507"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-508">
          <div className="max-w-3xl mx-auto text-center" data-id="element-509">
            <h2 className="text-white font-display font-bold mb-6 text-3xl md:text-4xl" data-id="element-510">
              READY TO SECURE YOUR FINANCIAL FUTURE?
            </h2>
            <p className="text-white/90 max-w-2xl mx-auto mb-10 text-lg font-body" data-id="element-511">
              Contact us today for a personalized consultation. Our expert
              advisors are ready to help you navigate your financial journey
              with confidence.
            </p>
            <a href="/contact" className="inline-flex items-center justify-center font-display font-bold text-sm tracking-widest rounded transition-all duration-300 px-8 py-4 hover:shadow-lg" style={{
            backgroundColor: '#FFFFFF',
            color: '#1a1a1a'
          }} data-id="element-512">
              GET IN TOUCH WITH US
            </a>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;